/* Aspetto del captcha con una barra degli strumenti «stile Word»: font, colore del testo, sfondo e anteprima vera dal bot. */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, Loader2, PaintBucket, RefreshCw, Search, Trash2, Upload, X } from "lucide-react";
import { apiError, botDelete, botGet, botPost } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { InfoTip } from "@/pages/tickets/ticketsKit";

const fontsApi = {
  list: () => botGet("verification/fonts"),
  preview: (style) => botPost("verification/captcha-preview", style),
  upload: (name, data) => botPost("verification/fonts/custom", { name, data }),
  remove: (id) => botDelete(`verification/fonts/custom/${id}`),
};

const DEFAULT = { font: { source: "builtin", family: null, id: null }, colors: [], background: "dark" };
const CATEGORIES = [["all", "Tutti"], ["styles", "✨ Stili Instagram"], ["Sans Serif", "Moderni"], ["Serif", "Classici"], ["Display", "Decorativi"], ["Handwriting", "A mano"], ["Monospace", "Macchina"]];
const RANDOM_BAR = "linear-gradient(90deg,#ED4245,#FEE75C,#57F287,#00A8FC,#8B5CF6,#EB459E)";

// ── colori come in Word: una riga di colori tema con le loro sfumature + i colori standard ──
const THEME = ["#FFFFFF", "#000000", "#5865F2", "#57F287", "#FEE75C", "#ED4245", "#EB459E", "#00A8FC", "#F47B67", "#8B5CF6"];
const STANDARD = ["#C00000", "#FF0000", "#FFC000", "#FFFF00", "#92D050", "#00B050", "#00B0F0", "#0070C0", "#002060", "#7030A0"];
const mix = (hex, target, t) => {
  const a = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const b = target === "white" ? [255, 255, 255] : [0, 0, 0];
  return `#${a.map((v, i) => Math.round(v + (b[i] - v) * t).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
};
const SHADES = [
  THEME,
  THEME.map((c, i) => (i === 0 ? "#F2F2F2" : i === 1 ? "#7F7F7F" : mix(c, "white", 0.6))),
  THEME.map((c, i) => (i === 0 ? "#D9D9D9" : i === 1 ? "#595959" : mix(c, "white", 0.35))),
  THEME.map((c, i) => (i === 0 ? "#BFBFBF" : i === 1 ? "#3F3F3F" : mix(c, "black", 0.25))),
  THEME.map((c, i) => (i === 0 ? "#A5A5A5" : i === 1 ? "#262626" : mix(c, "black", 0.5))),
];
const luminance = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Carica da Google Fonts i font visibili nell'elenco, per mostrare ogni nome con il suo carattere. */
const loadedCss = new Set();
function useGoogleCss(families) {
  useEffect(() => {
    const todo = families.filter((f) => f && !loadedCss.has(f));
    for (let i = 0; i < todo.length; i += 20) {
      const batch = todo.slice(i, i + 20);
      batch.forEach((f) => loadedCss.add(f));
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?${batch.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}`).join("&")}&display=swap`;
      document.head.appendChild(link);
    }
  }, [families]);
}

// ─────────────────────────── pezzi della barra ───────────────────────────
function Group({ label, info, children }) {
  return (
    <div className="flex flex-col items-center gap-1 px-2 first:pl-0 last:pr-0">
      <div className="flex items-center gap-1">{children}</div>
      <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#6b7185]">{label}<InfoTip text={info} className="h-3 w-3" /></span>
    </div>
  );
}
const Sep = () => <div className="mx-1 hidden h-12 w-px self-center bg-[#2a3045] sm:block" />;
const toolBtn = "flex h-9 items-center gap-1.5 rounded-md border border-transparent px-2 text-sm text-[#DBDEE1] transition-colors hover:border-[#3a4160] hover:bg-white/5 data-[state=open]:border-[#5865F2] data-[state=open]:bg-[#5865F2]/15";

function Swatch({ color, active, onClick, size = "h-5 w-5" }) {
  return (
    <button type="button" title={color} onClick={onClick}
      className={`${size} border transition-transform hover:z-10 hover:scale-125 ${active ? "ring-2 ring-white ring-offset-1 ring-offset-[#1b1f2e]" : ""}`}
      style={{ background: color, borderColor: luminance(color) > 0.85 ? "#9aa0b3" : luminance(color) < 0.05 ? "#3a4160" : "transparent" }} />
  );
}

// ── menu Carattere ──
function FontMenu({ style, set, fonts, reloadFonts }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [limit, setLimit] = useState(50);
  const google = useMemo(() => fonts?.google ?? [], [fonts]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (cat === "styles") return [];
    return google.filter((f) => (cat === "all" || f.c === cat) && (!s || f.f.toLowerCase().includes(s)));
  }, [google, q, cat]);
  const styles = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (cat !== "all" && cat !== "styles") return [];
    return (fonts?.styles ?? []).filter((x) => !s || x.name.toLowerCase().includes(s) || "instagram stili".includes(s));
  }, [fonts, q, cat]);
  const visible = useMemo(() => (open ? filtered.slice(0, limit) : []), [filtered, limit, open]);
  const families = useMemo(() => [...visible.map((f) => f.f), style.font.source === "google" ? style.font.family : null], [visible, style.font.source, style.font.family]);
  useGoogleCss(families);

  const cur = style.font;
  const label = cur.source === "builtin" ? "Base KALYX" : cur.source === "google" ? cur.family
    : cur.source === "style" ? fonts?.styles?.find((x) => x.key === cur.family)?.sample ?? "Stile Instagram" : fonts?.custom?.find((f) => f.id === cur.id)?.name ?? "Font caricato";
  const pick = (font) => { set({ font }); setOpen(false); };
  const removeCustom = async (id) => {
    try {
      await fontsApi.remove(id);
      await reloadFonts();
      if (cur.source === "custom" && cur.id === id) set({ font: DEFAULT.font });
      toast.success("Font eliminato");
    } catch (e) { toast.error(apiError(e)); }
  };
  const Item = ({ active, onClick, children, right, family }) => (
    <div className={`group flex items-center gap-2 rounded-md px-2.5 py-1.5 ${active ? "bg-[#5865F2]/25" : "hover:bg-white/5"}`}>
      <button type="button" onClick={onClick} className="min-w-0 flex-1 truncate text-left text-[17px] leading-snug text-white" style={family ? { fontFamily: `'${family}', sans-serif` } : undefined}>{children}</button>
      {active && <Check className="h-4 w-4 shrink-0 text-[#8ea0ff]" />}
      {right}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${toolBtn} w-52 justify-between border-[#2a3045] bg-[#131620]`} data-testid="font-menu">
          <span className="truncate text-[15px]" style={cur.source === "google" ? { fontFamily: `'${cur.family}', sans-serif` } : undefined}>{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-[#949BA4]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] border-[#2e3450] bg-[#1b1f2e] p-0 text-[#DBDEE1]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="space-y-2 border-b border-[#2a3045] p-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7185]" />
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setLimit(50); }} placeholder={`Cerca tra ${google.length || "…"} font`}
              className="h-9 w-full rounded-md border border-[#2a3045] bg-[#131620] pl-8 pr-2 text-sm text-white outline-none placeholder:text-[#5f6479] focus:border-[#5865F2]" data-testid="font-search" />
          </div>
          <div className="flex flex-wrap gap-1">
            {CATEGORIES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setCat(k); setLimit(50); }}
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cat === k ? "bg-[#5865F2] text-white" : "bg-[#131620] text-[#949BA4] hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="max-h-[360px] overflow-y-auto p-1.5">
          {!q && cat === "all" && (
            <>
              <p className="px-2.5 pb-1 pt-1 text-[10px] font-bold uppercase tracking-widest text-[#6b7185]">Font del bot</p>
              <Item active={cur.source === "builtin"} onClick={() => pick(DEFAULT.font)}>Base KALYX</Item>
              {fonts?.custom?.length > 0 && <p className="px-2.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-[#6b7185]">I tuoi font</p>}
              {fonts?.custom?.map((f) => (
                <Item key={f.id} active={cur.source === "custom" && cur.id === f.id} onClick={() => pick({ source: "custom", family: null, id: f.id })}
                  right={<button type="button" title="Elimina il font" onClick={() => removeCustom(f.id)} className="rounded p-1 text-[#F23F43] opacity-60 hover:bg-[#F23F43]/10 hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>}>{f.name}</Item>
              ))}
            </>
          )}
          {styles.length > 0 && (
            <>
              <p className="flex items-center gap-1 px-2.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-[#6b7185]">✨ Stili Instagram
                <InfoTip className="h-3 w-3" text="Gli stessi caratteri speciali dei generatori di font per Instagram (𝓐 𝔄 𝔸 Ⓐ 🅰 ᴀʙᴄ…). Chi si verifica vede le lettere con questo stile, ma scrive le lettere normali. La prima volta il bot scarica i caratteri: l'anteprima può metterci qualche secondo." />
              </p>
              {styles.map((x) => (
                <Item key={x.key} active={cur.source === "style" && cur.family === x.key} onClick={() => pick({ source: "style", family: x.key, id: null })}>
                  <span className="block truncate">{x.sample}</span>
                  <span className="block truncate font-sans text-[10px] text-[#6b7185]">{x.name} · {x.code}</span>
                </Item>
              ))}
              {cat === "all" && !q && <p className="px-2.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-[#6b7185]">Google Fonts</p>}
            </>
          )}
          {!fonts && <div className="flex items-center gap-2 px-2.5 py-3 text-sm text-[#949BA4]"><Loader2 className="h-4 w-4 animate-spin" />Carico i font…</div>}
          {fonts && filtered.length === 0 && styles.length === 0 && <p className="px-2.5 py-3 text-sm text-[#949BA4]">Nessun font con questo nome.</p>}
          {visible.map((f) => (
            <Item key={f.f} family={f.f} active={cur.source === "google" && cur.family === f.f} onClick={() => pick({ source: "google", family: f.f, id: null })}>{f.f}</Item>
          ))}
          {filtered.length > limit && (
            <button type="button" onClick={() => setLimit(limit + 80)} className="mt-1 w-full rounded-md py-2 text-xs font-semibold text-[#8ea0ff] hover:bg-white/5">Mostra altri ({filtered.length - limit})</button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── menu Colore carattere ──
function ColorMenu({ style, set }) {
  const [open, setOpen] = useState(false);
  const [multi, setMulti] = useState(style.colors.length > 1);
  const colors = style.colors;
  const bar = colors.length === 0 ? RANDOM_BAR : colors.length === 1 ? colors[0] : `linear-gradient(90deg,${colors.join(",")})`;
  const choose = (c) => {
    if (multi) {
      if (colors.includes(c)) set({ colors: colors.filter((x) => x !== c) });
      else if (colors.length < 8) set({ colors: [...colors, c] });
      else toast.error("Massimo 8 colori");
    } else {
      set({ colors: [c] });
      setOpen(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center">
        <button type="button" title="Colore del testo" onClick={() => setOpen(true)} className={`${toolBtn} flex-col justify-center gap-0 rounded-r-none px-2.5`}>
          <span className="text-lg font-black leading-none">A</span>
          <span className="mt-0.5 h-1.5 w-6 rounded-sm" style={{ background: bar }} />
        </button>
        <PopoverTrigger asChild>
          <button type="button" className={`${toolBtn} rounded-l-none px-1`} data-testid="color-menu"><ChevronDown className="h-4 w-4 text-[#949BA4]" /></button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" className="w-[268px] border-[#2e3450] bg-[#1b1f2e] p-3 text-[#DBDEE1]">
        <button type="button" onClick={() => { set({ colors: [] }); setMulti(false); setOpen(false); }}
          className={`mb-3 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm ${colors.length === 0 ? "bg-[#5865F2]/25 text-white" : "hover:bg-white/5"}`}>
          <span className="h-4 w-4 rounded-sm" style={{ background: RANDOM_BAR }} />Automatico (colori casuali){colors.length === 0 && <Check className="ml-auto h-4 w-4 text-[#8ea0ff]" />}
        </button>
        <p className="mb-1.5 text-[11px] font-semibold text-[#949BA4]">Colori tema</p>
        <div className="space-y-0.5">
          {SHADES.map((row, i) => (
            <div key={i} className={`flex justify-between ${i === 0 ? "mb-1.5" : ""}`}>
              {row.map((c) => <Swatch key={c + i} color={c} active={colors.includes(c)} onClick={() => choose(c)} />)}
            </div>
          ))}
        </div>
        <p className="mb-1.5 mt-3 text-[11px] font-semibold text-[#949BA4]">Colori standard</p>
        <div className="flex justify-between">{STANDARD.map((c) => <Swatch key={c} color={c} active={colors.includes(c)} onClick={() => choose(c)} />)}</div>
        <label className="relative mt-3 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-white/5">
          <span className="h-4 w-4 rounded-sm border border-[#3a4160]" style={{ background: "conic-gradient(red,yellow,lime,cyan,blue,magenta,red)" }} />Altri colori…
          <input type="color" value={colors[colors.length - 1] || "#5865F2"} onChange={(e) => choose(e.target.value.toUpperCase())} className="absolute inset-0 cursor-pointer opacity-0" />
        </label>
        <div className="mt-2 border-t border-[#2a3045] pt-2.5">
          <label className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-1">Più colori insieme<InfoTip text="Accendilo per scegliere più colori: ogni lettera del codice prende a caso uno dei colori selezionati (fino a 8). Spento: tutte le lettere dello stesso colore." /></span>
            <Switch checked={multi} onCheckedChange={(m) => { setMulti(m); if (!m && colors.length > 1) set({ colors: [colors[0]] }); }} />
          </label>
          {multi && colors.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {colors.map((c) => (
                <span key={c} className="flex items-center gap-1 rounded-full bg-[#131620] py-0.5 pl-0.5 pr-1.5 text-[10px] font-mono">
                  <span className="h-4 w-4 rounded-full border border-white/20" style={{ background: c }} />{c}
                  <button type="button" onClick={() => set({ colors: colors.filter((x) => x !== c) })} className="text-[#949BA4] hover:text-white"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── menu Sfondo ──
function BackgroundMenu({ style, set }) {
  const [open, setOpen] = useState(false);
  const bg = style.background === "light" ? "#E9ECF3" : "#1E2130";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Sfondo" className={`${toolBtn} flex-col justify-center gap-0 px-2.5`} data-testid="bg-menu">
          <PaintBucket className="h-4 w-4" />
          <span className="mt-0.5 h-1.5 w-6 rounded-sm border border-white/20" style={{ background: bg }} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 border-[#2e3450] bg-[#1b1f2e] p-1.5 text-[#DBDEE1]">
        {[["dark", "Scuro", "#1E2130"], ["light", "Chiaro", "#E9ECF3"]].map(([k, l, c]) => (
          <button key={k} type="button" onClick={() => { set({ background: k }); setOpen(false); }}
            className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm ${style.background === k ? "bg-[#5865F2]/25 text-white" : "hover:bg-white/5"}`}>
            <span className="h-5 w-8 rounded border border-white/20" style={{ background: c }} />{l}{style.background === k && <Check className="ml-auto h-4 w-4 text-[#8ea0ff]" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────── componente ───────────────────────────
export default function CaptchaStyle({ value, onChange }) {
  const style = { ...DEFAULT, ...(value || {}), font: { ...DEFAULT.font, ...(value?.font || {}) } };
  const set = (p) => onChange({ ...style, ...p });
  const [fonts, setFonts] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pErr, setPErr] = useState(null);
  const [pBusy, setPBusy] = useState(false);
  const [seed, setSeed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const reloadFonts = () => fontsApi.list().then(setFonts).catch((e) => toast.error(apiError(e)));
  useEffect(() => { reloadFonts(); }, []);

  // Anteprima vera, disegnata dal bot con le impostazioni attuali (anche non salvate).
  const key = JSON.stringify(style);
  useEffect(() => {
    let alive = true;
    setPBusy(true);
    const t = setTimeout(() => {
      fontsApi.preview(style)
        .then((r) => { if (alive) { setPreview(r.image); setPErr(null); } })
        .catch((e) => { if (alive) setPErr(apiError(e)); })
        .finally(() => alive && setPBusy(false));
    }, 300);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, seed]);

  const upload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Il font è troppo grande (massimo 5 MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const entry = await fontsApi.upload(file.name, reader.result);
        await reloadFonts();
        set({ font: { source: "custom", family: null, id: entry.id } });
        toast.success(`Font «${entry.name}» caricato e selezionato`);
      } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };
    reader.readAsDataURL(file);
  };
  const badColors = style.colors.filter((c) => (style.background === "light" ? luminance(c) > 0.75 : luminance(c) < 0.15));

  return (
    <div className="overflow-hidden rounded-2xl border border-[#2a3045] bg-[#0f111a]" data-testid="captcha-style">
      {/* barra degli strumenti */}
      <div className="flex flex-wrap items-end gap-y-2 border-b border-[#2a3045] bg-[#1b1f2e] px-3 py-2">
        <Group label="Carattere" info="Il font con cui è scritto il codice. Apri il menu per scegliere tra il font del bot, i tuoi font caricati e circa 1800 Google Fonts (ogni nome è scritto con il suo font). Vanno bene solo i font che hanno tutte le lettere e i numeri dei codici.">
          <FontMenu style={style} set={set} fonts={fonts} reloadFonts={reloadFonts} />
          <input ref={fileRef} type="file" accept=".ttf,.otf,.woff" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" title="Carica un tuo font (.ttf, .otf, .woff)" disabled={uploading} onClick={() => fileRef.current?.click()} className={toolBtn}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}<span className="hidden text-xs sm:inline">Carica</span>
          </button>
        </Group>
        <Sep />
        <Group label="Colore" info="Il colore delle lettere. «Automatico» = ogni lettera un colore acceso diverso. Scegli un colore dalla tavolozza per averle tutte uguali, oppure accendi «Più colori insieme» per sceglierne diversi.">
          <ColorMenu style={style} set={set} />
        </Group>
        <Sep />
        <Group label="Sfondo" info="Il colore di fondo dell'immagine: scuro o chiaro. Con lo sfondo chiaro i colori automatici diventano scuri, per restare leggibili.">
          <BackgroundMenu style={style} set={set} />
        </Group>
        <Sep />
        <Group label="Anteprima" info="Genera un altro codice di prova, per vedere come vengono lettere diverse.">
          <button type="button" onClick={() => setSeed(seed + 1)} className={toolBtn} title="Altro codice"><RefreshCw className={`h-4 w-4 ${pBusy ? "animate-spin" : ""}`} /><span className="text-xs">Altro codice</span></button>
        </Group>
      </div>

      {/* «foglio» con l'anteprima vera */}
      <div className="flex flex-col items-center gap-3 bg-[#0b0d14] px-4 py-6">
        <div className="w-full max-w-[520px] rounded-xl bg-[#313338] p-4 shadow-2xl">
          <p className="mb-2 text-sm text-[#DBDEE1]">🔐 <b>Ricopia il codice che vedi nell'immagine</b>, poi premi <b>Inserisci il codice</b>.</p>
          <div className="relative overflow-hidden rounded-lg bg-[#1e1f22]" style={{ aspectRatio: "320 / 110" }}>
            {preview && <img src={preview} alt="Anteprima del captcha" className="h-full w-full" data-testid="captcha-preview" />}
            {!preview && !pErr && <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#949BA4]" /></div>}
            {pBusy && preview && <div className="absolute inset-0 flex items-center justify-center bg-black/25"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
          </div>
          <div className="mt-2 flex gap-2">
            <span className="rounded bg-[#248046] px-3 py-1.5 text-xs font-medium text-white">✏️ Inserisci il codice</span>
            <span className="rounded bg-[#4E5058] px-3 py-1.5 text-xs font-medium text-white">🔄 Nuovo codice</span>
          </div>
        </div>
        {pErr && <p className="w-full max-w-[520px] rounded-xl border border-[#F23F43]/40 bg-[#F23F43]/10 px-3 py-2 text-xs text-[#ffb3b4]">{pErr}</p>}
        {badColors.length > 0 && <p className="text-xs text-[#FEE75C]">⚠ {badColors.join(", ")} {badColors.length > 1 ? "si leggono" : "si legge"} poco su sfondo {style.background === "light" ? "chiaro" : "scuro"}.</p>}
        <p className="text-[11px] text-[#6b7185]">Anteprima vera, disegnata dal bot: la vedrà così chi entra. Ricordati di salvare.</p>
      </div>
    </div>
  );
}
